import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { CourseDetailPage } from '@/pages/public/course-detail-page'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getPublicCourseCatalog, getPublicSessionById } from '@/lib/api'
import { queryKeys } from '@/lib/query-keys'

export function LegacyCourseBookingRedirect() {
  const navigate = useNavigate()
  const { sessionId } = useParams<{ sessionId: string }>()

  const sessionQuery = useQuery({
    queryKey: sessionId ? queryKeys.publicSession(sessionId) : ['public-session', 'none'],
    queryFn: () => getPublicSessionById(sessionId as string),
    enabled: Boolean(sessionId),
    retry: false,
  })

  useEffect(() => {
    if (!sessionQuery.data) {
      return
    }

    navigate(
      `/kurse/${sessionQuery.data.course_id}/termine/${sessionQuery.data.session_id}`,
      { replace: true },
    )
  }, [navigate, sessionQuery.data])

  if (sessionQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    )
  }

  if (sessionQuery.isError) {
    return (
      <Card>
        <CardContent className="space-y-4 p-6">
          <p className="text-sm text-destructive">Der gewünschte Termin konnte nicht geladen werden.</p>
          <Button asChild variant="outline">
            <Link to="/kurse">Zurück zu den Kursen</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return null
}

export function LegacyCourseDetailRedirect() {
  const navigate = useNavigate()
  const { courseId, sessionId } = useParams<{ courseId?: string; sessionId?: string }>()

  useEffect(() => {
    if (!courseId) {
      return
    }

    navigate(
      sessionId ? `/kurse/${courseId}/termine/${sessionId}` : `/kurse/${courseId}`,
      { replace: true },
    )
  }, [courseId, navigate, sessionId])

  return null
}

export function CourseRouteResolver() {
  const { id } = useParams<{ id: string }>()

  const coursesQuery = useQuery({
    queryKey: queryKeys.publicCourseCatalog,
    queryFn: getPublicCourseCatalog,
  })

  const isKnownCourse = (coursesQuery.data ?? []).some((course) => course.id === id)

  const sessionQuery = useQuery({
    queryKey: id ? queryKeys.publicSession(id) : ['public-session', 'none'],
    queryFn: () => getPublicSessionById(id as string),
    enabled: Boolean(id) && !coursesQuery.isLoading && !isKnownCourse,
    retry: false,
  })

  const navigate = useNavigate()

  useEffect(() => {
    if (!sessionQuery.data) {
      return
    }

    navigate(`/kurse/${sessionQuery.data.course_id}/termine/${sessionQuery.data.session_id}`, {
      replace: true,
    })
  }, [navigate, sessionQuery.data])

  if (coursesQuery.isLoading || sessionQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    )
  }

  if (isKnownCourse && id) {
    return <CourseDetailPage courseIdOverride={id} />
  }

  if (sessionQuery.isError) {
    return (
      <Card>
        <CardContent className="space-y-4 p-6">
          <p className="text-sm text-destructive">Der gewünschte Kurs konnte nicht geladen werden.</p>
          <Button asChild variant="outline">
            <Link to="/kurse">Zurück zu den Kursen</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return null
}
